import type { SynthAssetKeysV3, TokenTickersAndEth } from '@kwenta/sdk/types'
import { getDisplayAsset } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import type { CellContext } from '@tanstack/react-table'
import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import { type FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Currency from 'components/Currency'
import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import { TransactionPill } from 'components/Pill'
import Spacer from 'components/Spacer'
import Table, { TableHeader, TableNoResults } from 'components/Table'
import { TableCell } from 'components/Table/TableBodyRow'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import {
	selectAssetsV3,
	selectAvailableMarginQueryStatuses,
} from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { FetchStatus } from '../../../state/types'

import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import { useMarketPageRoute } from 'hooks/useMarketPageRoute'
import { DrawerTab } from 'sections/accounts/constants'
import TableMarketDetails from './TableMarketDetails'

type CellProps = CellContext<
	{
		asset: SynthAssetKeysV3
		token: TokenTickersAndEth
		walletBalance: {
			size: string
			value: string
		}
		marginBalance: {
			size: string
			value: string
		}
	},
	any
>

type Props = {
	primaryBackground?: boolean
}

const AssetsTable: FC<Props> = memo(({ primaryBackground }: Props) => {
	const { t } = useTranslation()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const assets = useAppSelector(selectAssetsV3, isEqual)
	const availableMarginStatus = useAppSelector(selectAvailableMarginQueryStatuses)
	const columnsDeps = useMemo(() => [assets], [assets])

	const isLoading = useMemo(
		() => !assets.length && availableMarginStatus === FetchStatus.Loading,
		[assets.length, availableMarginStatus]
	)

	return (
		<Container>
			<DesktopOnlyView>
				<TableContainer primaryBackground={primaryBackground}>
					<Table
						highlightRowsOnHover
						rounded={false}
						noBottom={false}
						autoResetPageIndex={false}
						showPagination={false}
						columns={[
							{
								header: () => <TableHeader>{t('futures.market.user.assets.asset')}</TableHeader>,
								accessorKey: 'assetName',
								cell: (cellProps) => (
									<TableMarketDetails
										marketName={cellProps.row.original.token}
										assetKey={cellProps.row.original.token}
										iconProps={{ width: 28, height: 28 }}
										perpsProvider={perpsProvider}
									/>
								),
								enableSorting: true,
							},
							{
								header: () => (
									<TableHeader>{t('futures.market.user.assets.margin-balance')}</TableHeader>
								),
								accessorKey: 'marginBalance',
								cell: (cellProps) => <AssetBalance {...cellProps} />,
								enableSorting: true,
							},
							{
								header: () => (
									<TableHeader>{t('futures.market.user.assets.wallet-balance')}</TableHeader>
								),
								accessorKey: 'walletBalance',
								cell: (cellProps) => <AssetBalance {...cellProps} />,
								enableSorting: true,
							},
							{
								header: () => <></>,
								accessorKey: 'actions',
								cell: (cellProps) => <ActionButtons {...cellProps} />,
								enableSorting: false,
							},
						]}
						columnsDeps={columnsDeps}
						data={assets}
						isLoading={isLoading && !assets?.length}
						noResultsMessage={
							!isLoading && assets?.length === 0 ? (
								<TableNoResults>{t('futures.market.user.assets.no-results')}</TableNoResults>
							) : undefined
						}
					/>
				</TableContainer>
			</DesktopOnlyView>
			<MobileOrTabletView>
				<MobileTableContainer>
					<MobileTable
						highlightRowsOnHover
						rounded={false}
						noBottom={false}
						autoResetPageIndex={false}
						showPagination={false}
						columns={[
							{
								accessorKey: 'marginBalance',
								cell: (cellProps) => {
									return (
										<MobileColContainer>
											<TableMarketDetails
												marketName={cellProps.row.original.token}
												assetKey={cellProps.row.original.token}
												iconProps={{ width: 28, height: 28 }}
												perpsProvider={perpsProvider}
											/>
											<FlexDivCol>
												<Body color="secondary">
													{t('futures.market.user.assets.margin-balance')}
												</Body>
												<AssetBalance {...cellProps} />
											</FlexDivCol>
										</MobileColContainer>
									)
								},
							},
							{
								accessorKey: 'walletBalance',
								cell: (cellProps) => {
									return (
										<MobileColContainer>
											<ActionButtons {...cellProps} />
											<FlexDivCol>
												<Body color="secondary">
													{t('futures.market.user.assets.wallet-balance')}
												</Body>
												<AssetBalance {...cellProps} />
											</FlexDivCol>
										</MobileColContainer>
									)
								},
							},
						]}
						columnsDeps={columnsDeps}
						data={assets}
						isLoading={isLoading && !assets?.length}
						noResultsMessage={
							!isLoading && assets?.length === 0 ? (
								<TableNoResults>{t('futures.market.user.assets.no-results')}</TableNoResults>
							) : undefined
						}
					/>
				</MobileTableContainer>
			</MobileOrTabletView>
		</Container>
	)
})

export default AssetsTable

const AssetBalance: FC<CellProps> = (cellProps) => {
	return (
		<FlexDivRowCentered columnGap="5px">
			<div>
				<BalanceContainer
					noBalance={!wei(cellProps.getValue().size).gt(0)}
					justifyContent="flex-start"
					columnGap="5px"
				>
					<Currency.Price
						price={cellProps.getValue().size}
						currencyKey={cellProps.row.original.asset}
						formatOptions={{ suggestDecimals: true }}
					/>
					<Body>{getDisplayAsset(cellProps.row.original.asset) ?? ''}</Body>
				</BalanceContainer>
				<Currency.Price
					price={cellProps.getValue().value}
					formatOptions={{ truncateOver: 1e6, maxDecimals: 2 }}
					colorType="secondary"
				/>
			</div>
		</FlexDivRowCentered>
	)
}

const ActionButtons: FC<CellProps> = (cellProps) => {
	const { t } = useTranslation()
	const router = useRouter()
	const dispatch = useAppDispatch()
	const marketPageRoute = useMarketPageRoute()
	const { trackWithdrawFlow } = useAnalytics()

	return (
		<FlexDivRowCentered>
			<TransactionPill
				onClick={() => {
					trackWithdrawFlow(EVENT_NAME.CLICK_DEPOSIT)
					dispatch(
						setOpenModalWithParams({
							type: 'manage_wallet',
							params: { tab: DrawerTab.DEPOSIT, asset: cellProps.row.original.token },
						})
					)
					if (!router.pathname.includes('market')) {
						router.push(marketPageRoute())
					}
				}}
				disabled={wei(cellProps.row.original.walletBalance.size).eq(0)}
				size="medium"
			>
				{t('futures.market.user.assets.deposit')}
			</TransactionPill>
			<Spacer width={10} />
			<TransactionPill
				onClick={() => {
					trackWithdrawFlow(EVENT_NAME.CLICK_WITHDRAW)
					dispatch(
						setOpenModalWithParams({
							type: 'manage_wallet',
							params: { tab: DrawerTab.WITHDRAW, asset: cellProps.row.original.token },
						})
					)
					if (!router.pathname.includes('market')) {
						router.push(marketPageRoute())
					}
				}}
				disabled={wei(cellProps.row.original.marginBalance.size).eq(0)}
				size="medium"
			>
				{t('futures.market.user.assets.withdraw')}
			</TransactionPill>
		</FlexDivRowCentered>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: row;
	flex: 1;
	height: 100%;
`

const BalanceContainer = styled(FlexDivRowCentered)<{ noBalance: boolean }>`
	* {
		color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.text[props.noBalance ? 'secondary' : 'primary']};
	}
`

const MobileTableContainer = styled(FlexDivCol)`
	width: 100%;
`

const MobileTable = styled(Table)`
	.table-row:first-child {
		display: none;
	}

	.table-body {
		max-height: 100%;
	}

	.table-body-row:first-child {
		padding-top: 15px;
	}

	.table-body-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-template-rows: auto;
		grid-column-gap: 15px;
		grid-row-gap: 15px;
		padding: 15px 0px;
		margin: 0 20px;

		& > div:last-child > div > div:first-child {
			justify-content: flex-end;
			height: 30px;
		}
	}

	${TableCell}:first-child,
	${TableCell}:last-child {
		padding: 0;
	}
	${TableCell} {
		height: 100%;
		width: 100% !important;
		display: flex;
		align-items: start;
		justify-content: space-between;
	}
	border: none;
` as typeof Table

const TableContainer = styled.div<{
	primaryBackground?: boolean
}>`
	background-color: ${(props) =>
		props.primaryBackground
			? props.theme.colors.selectedTheme.newTheme.containers.primary.background
			: 'none'};
			
	display: flex;
	flex-direction: column;
	width: 100%;

	.table-body-row {
		border-bottom: none;
	}

	.table-row > div:not(:first-child) {
		padding-left: 0;
	}

	.table-body-row > div:last-child {
		justify-content: flex-end;
		max-width: 150px;
	}
	.table-row > div:last-child {
		max-width: 150px;
	}

	.table-row > div:first-child {
		justify-content: flex-start;
	}

	.table-row > div:last-child {
		justify-content: flex-end;
	}

	.table-body-row > div:first-child {
		justify-content: flex-start;
	}

	.table-body-row > div:last-child {
		justify-content: flex-end;
	}
`

const MobileColContainer = styled(FlexDivCol)`
	width: 100%;
	justify-content: flex-start;
	gap: 13px;
`
